import { getApiUrl } from '@/config/api'

// URL builders for the coaching conversation endpoints (ENG-3371, ENG-3374). Each URL is also the SWR key.

import type { ThreadCursor } from '@/types/coachMessages'

export const coachMessagesService = {
  /** The coach's own list. Scoped to the caller, so it takes no id. */
  getConversationsUrl: (limit: number, offset: number) =>
    getApiUrl(`/coaching/conversations?limit=${limit}&offset=${offset}`),

  /** Another coach's list, as that coach sees it (ENG-4087). Coaching managers only. */
  getCoachConversationsUrl: (coachUserId: string, limit: number, offset: number) =>
    getApiUrl(`/coaching/coaches/${coachUserId}/conversations?limit=${limit}&offset=${offset}`),

  /**
   * One page of a learner's conversation, newest first.
   *
   * The same URL serves a coach and the learner themselves; the API decides
   * which the caller is. `POST` to the id-less form of this URL writes into the
   * conversation.
   */
  getThreadUrl: (learnerUserId: string, limit: number, before?: ThreadCursor | null) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (before) {
      params.set('before_created_at', before.created_at)
      params.set('before_id', before.id)
    }
    return getApiUrl(`/coaching/learners/${learnerUserId}/messages?${params.toString()}`)
  },

  /** Where a message is written. Also the learner's reply, which the API routes. */
  getMessagesUrl: (learnerUserId: string) => getApiUrl(`/coaching/learners/${learnerUserId}/messages`),

  getMarkReadUrl: (learnerUserId: string) => getApiUrl(`/coaching/learners/${learnerUserId}/messages/read`),

  /**
   * Who this learner is and how a coach can reach them.
   *
   * Its own key rather than a field on the thread, because the thread pages and
   * this does not: a coach scrolling back through two years of messages would
   * otherwise re-fetch an unchanging profile on every page.
   */
  getContextUrl: (learnerUserId: string) => getApiUrl(`/coaching/learners/${learnerUserId}/context`),

  /** Record the learner's do-not-contact decision for outbound email and SMS. */
  getDoNotContactUrl: (learnerUserId: string) => getApiUrl(`/coaching/learners/${learnerUserId}/do-not-contact`),

  /**
   * Who is coaching this learner, for their own page.
   *
   * Readable by the learner themselves, unlike `getContextUrl` above, and it is
   * the one coaching read that answers when there is *no* coach: the page
   * branches on its `state`.
   */
  getMyCoachUrl: (learnerUserId: string) => getApiUrl(`/coaching/learners/${learnerUserId}/coach`),

  /**
   * Delivery verdicts for the messages just read.
   *
   * Staff only. `message_ids` repeats once per id, which is what FastAPI's
   * repeated-query-parameter binding expects; an empty list has no URL, because
   * the API requires at least one and a caller with nothing to ask should not
   * ask.
   */
  getDeliveryUrl: (learnerUserId: string, messageIds: readonly string[]) => {
    const params = new URLSearchParams()
    for (const id of messageIds) params.append('message_ids', id)
    return getApiUrl(`/coaching/learners/${learnerUserId}/messages/delivery?${params.toString()}`)
  },
}
